<div class="flex gap-4 align-items-center mb-3">
  @if (!isKioskMode()) {
    <button
      class="!min-w-12"
      cue-button
      size="small"
      (click)="requestCloseTargetSelection.emit()"
      data-cy="target-selection-back"
    >
      <cue-icon [icon]="ICONS.cueArrowFullLeft" />
    </button>
  }
  <h2 class="m-0 start-title">
    {{ translation.wayfinding.target | transloco }}
  </h2>
</div>
<div
  class="inner-content flex flex-col target-height overflow-hidden"
  [class.half-width]="showNavigationFilter() || isKioskMode()"
>
  <assist-textbox
    data-cy="target-search-input"
    [showClear]="true"
    [placeholder]="targetResourceName() || (translation.navigation.placeholder | transloco)"
    [formControl]="search"
    [iconBefore]="ICONS.cueSearch"
  />

  <div class="flex flex-row overflow-hidden gap-3 grow">
    <!--      CONTAINER WITH FILTERS FOR KIOSK MODE-->
    <ng-container>
      <div class="hidden items-start overflow-auto pr-2 mt-3 pb-2 min-w-40">
        <div class="categories flex-col relative items-start">
          <p class="!my-1 font-bold text-[--text-description] pb-1">
            {{ translation.navigation.filters | transloco }}
          </p>
          <button
            class="!absolute !right-0"
            cue-button
            [variant]="
              (!showNavigationFilter() && selectedCategoryIdList().length === 0) ||
              selectedAreaIdList().length > 0 ||
              selectedResourceTypeIdList().length > 0
                ? 'secondary'
                : 'primary'
            "
            size="small"
            data-cy="target-show-filter-kiosk-mode"
            (click)="changeShowFilter(!showNavigationFilter())"
          >
            <cue-icon [icon]="ICONS.cueFilter" />
          </button>

          @for (item of categories.value() | categoryListResourcesOnly; track item; let i = $index) {
            <button
              cue-toggle-button
              [active]="getIndexOfSelectedItem(item.resourceTypeId, selectedResourceTypeIdList()) >= 0"
              (click)="filterToSpecificResourceType(item)"
            >
              @if (!!item.iconUrl) {
                <img [src]="item.iconUrl" class="h-5 w-5 max-w-none" loading="lazy" />
              }
              {{ item.name | localizedString }}
            </button>
            @if (categories.value().length !== i + 1) {
              <div class="divider my-1"></div>
            }
          }
          @for (item of categories.value() | categoryListCategoriesOnly; track item; let i = $index) {
            <button
              cue-toggle-button
              [active]="getIndexOfSelectedItem(item.qrCategoryId, selectedCategoryIdList()) >= 0"
              (click)="filterToSpecificCategory(item)"
            >
              @if (!!item.iconUrl) {
                <img [src]="item.iconUrl" class="h-5 w-5 max-w-none" />
              }
              {{ item.name | localizedString }}
            </button>
            @if (categories.value().length !== i + 1) {
              <div class="divider my-1"></div>
            }
          }
        </div>
      </div>
    </ng-container>

    <div class="h-full flex flex-col overflow-auto flex-1">
      <!--      CONTAINER WITH FILTERS FOR NON KIOSK MODE-->
      <ng-container>
        <div class="filters-info">
          <div class="filters-info-left">
            <button
              [link]="!showNavigationFilter() && selectedCategoryIdList().length === 0"
              cue-button
              size="small"
              data-cy="target-show-filter"
              (click)="changeShowFilter(!showNavigationFilter())"
              class="show-filter-btn"
              [class.outlined]="!showNavigationFilter()"
            >
              <cue-icon [icon]="ICONS.cueFilter" />
              {{ translation.navigation.filters | transloco }}
            </button>
            <div class="info-content">
              @if (activeFiltersCount() > 0) {
                <div class="active-filters" data-cy="navigation-active-filter-count">
                  {{ activeFiltersCount() }} active filters
                </div>
                <div class="filter-types">
                  @if (selectedResourceTypeIdList().length > 0) {
                    <button
                      class="filter-type-btn"
                      cue-button
                      size="nano"
                      [link]="true"
                      (click)="clearTypeFilter()"
                      data-cy="clear-type-filter-target"
                    >
                      <cue-icon [icon]="ICONS.cueClose" />
                      <span class="text">{{ translation.navigation.clearTypeFilter | transloco }}</span>
                    </button>
                  }
                  @if (selectedCategoryIdList().length > 0) {
                    <button
                      class="filter-type-btn"
                      cue-button
                      size="nano"
                      [link]="true"
                      (click)="clearCategoryFilter()"
                      data-cy="clear-category-filter-target"
                    >
                      <cue-icon [icon]="ICONS.cueClose" />
                      <span class="text">{{ translation.navigation.clearCategoryFilter | transloco }}</span>
                    </button>
                  }
                  @if (selectedAreaIdList().length > 0) {
                    <button
                      class="filter-type-btn"
                      cue-button
                      size="nano"
                      [link]="true"
                      (click)="clearAreaFilter()"
                      data-cy="clear-area-filter-target"
                    >
                      <cue-icon [icon]="ICONS.cueClose" />
                      <span class="text">{{ translation.navigation.clearAreaFilter | transloco }}</span>
                    </button>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      </ng-container>

      @if (startData() && (selectedCategoryIdList().length > 0 || selectedResourceTypeIdList().length > 0)) {
        <div class="switch mt-3">
          <button cue-button [class.all]="selectedSearchType === searchTypeEnum.Nearest"></button>
          <button
            data-cy="alphabetically-filter-button"
            class="value"
            (click)="selectedSearchType = searchTypeEnum.Alhabetical"
            [class.active]="selectedSearchType === searchTypeEnum.Alhabetical"
          >
            <cue-icon [icon]="ICONS.cueAppsSort" />
            {{ translation.navigation.sortABC | transloco }}
          </button>
          <button
            data-cy="from-the-nearest-filter-button"
            class="value"
            (click)="selectedSearchType = searchTypeEnum.Nearest"
            [class.active]="selectedSearchType === searchTypeEnum.Nearest"
          >
            <cue-icon [icon]="ICONS.cueMarker" />
            {{ translation.navigation.sortNearest | transloco }}
          </button>
        </div>
      }

      @if (selectedSearchType === searchTypeEnum.Alhabetical) {
        @if (basicListDataSource.isLoading()) {
          <cue-loading />
        }
        @if (basicListDataSource.hasValue()) {
          @if (basicListDataSource.value()?.total === 0) {
            <div class="not-found" data-cy="target-no-results">
              <p class="m-0 py-3">
                {{ translation.notFound.resourceNotFound | transloco }}
              </p>
            </div>
          }

          @if (basicListDataSource.value()?.total !== 0) {
            <div class="location-table grow h-full">
              <div class="fade-border flex overflow-hidden w-full grow">
                <div class="flex gap-2 flex-col overflow-auto w-full">
                  <cdk-virtual-scroll-viewport
                    class="grow *:pt-7 scrollbar-thick"
                    cueDynamicScrollSize
                    [itemSize]="search?.value ? 120 : 100"
                    minBufferPx="50"
                    maxBufferPx="100"
                    (scrolledIndexChange)="onScrolledIndexChange($event, basicListDataSource.value()!.alphabetInfo)"
                  >
                    <ng-container *cdkVirtualFor="let item of basicListDataSource.value(); index as i; let last = last">
                      <div class="cdk-scroll-item" [class.has-search-result]="search?.value">
                        @if (i === 0) {
                          <div [id]="'startPosition'"></div>
                        }

                        @if (item) {
                          <div class="relative item-wrapper">
                            <hr class="divider-hr" />

                            @if (item.letter) {
                              <h3 class="desktop-l-h3 item-letter" [id]="item.letter">
                                {{ item.letter }}
                              </h3>
                            }

                            <div
                              class="item"
                              [attr.data-cy]="'target-result-' + (item.resourceId || item.qrCodeId)"
                              [class.selected]="
                                (item.resourceId && item.resourceId === targetResourceId()) ||
                                (item.qrCodes?.length === 1 && item.qrCodes[0].qrCodeId === targetQrCodeId())
                              "
                              (click)="navigate(item, $event, false)"
                            >
                              <div class="item-inner">
                                <div
                                  class="preview-image"
                                  [class.preview-image--bordered]="
                                    item.icon && item.file && !hasImageLoadFailed(item.file)
                                  "
                                >
                                  @if (item.file && !hasImageLoadFailed(item.file)) {
                                    <img
                                      loading="lazy"
                                      [ngStyle]="{
                                        'object-position': item.imageObjectPosition,
                                      }"
                                      [class.img-cover]="item.imagePreferCover"
                                      [class.category-icon]="item.fileExtension === 'svg'"
                                      [src]="item.file | imageWidth: 'auto'"
                                      (error)="imageLoadFailed(item.file)"
                                    />
                                  } @else if (getCategoryIconUrl(item); as categoryIconUrl) {
                                    <img
                                      [src]="categoryIconUrl | imageWidth: 'auto'"
                                      (error)="imageLoadFailed(categoryIconUrl)"
                                    />
                                  } @else {
                                    <cue-icon
                                      [icon]="ICONS.cueQrCode"
                                      (click)="navigate(item, $event, false)"
                                      class="qr-img"
                                    />
                                  }
                                  @if (
                                    item.icon &&
                                    item.file &&
                                    !hasImageLoadFailed(item.file) &&
                                    !hasImageLoadFailed(item.icon)
                                  ) {
                                    <div class="item-icon-wrapper">
                                      <img class="item-icon" [src]="item.icon" (error)="imageLoadFailed(item.icon)" />
                                    </div>
                                  }
                                </div>
                                <div class="flex h-full w-full min-w-0 k-gap-1">
                                  <div class="header-content h-full w-full overflow-hidden">
                                    <div class="line-clamp-2" #targetABCTitleEl>
                                      <span class="rendered-html" data-cy="concrete-target-abc">
                                        {{
                                          (item.displayNameForApp | localizedString) ?? (item.name | localizedString)
                                        }}
                                      </span>
                                    </div>
                                    <div class="relative max-h-11 flex overflow-hidden">
                                      <div
                                        class="self-end"
                                        [class.truncate]="targetABCTitleEl.offsetHeight > 30"
                                        [class.rtl]="targetABCTitleEl.offsetHeight > 30"
                                      >
                                        @for (item of item.location; track item; let i = $index) {
                                          <!--                                                                          JESTLI SEM NEKDO ZASE NARVE SVGcko, protoze ma o 4.56 stupně otevřenější zobáček a sežere tim drahovaný výkon, tak mě pěkně vytočí-->
                                          @if (i > 0) {
                                            <span> > </span>
                                          }
                                          <span>{{ item | localizedString }}</span>
                                        }
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  cue-button
                                  class="just-icon detail-info-icon"
                                  [link]="true"
                                  size="small"
                                  (click)="openItemDetail(item, $event)"
                                  data-cy="target-show-item-detail-abc"
                                >
                                  <cue-icon [icon]="ICONS.cueInfoNoCircle" />
                                </button>
                              </div>

                              @if (search?.value) {
                                <div
                                  class="search-result"
                                  [class.show]="item.searchResultIsShown"
                                  (click)="toggleShow(item, $event)"
                                >
                                  <span class="label">{{ translation.wayfinding.searched | transloco }}: </span>
                                  <div class="search-result-items">
                                    @for (
                                      searchResultItem of item | searchResult: search?.value;
                                      track searchResultItem
                                    ) {
                                      <div
                                        class="search-result-item"
                                        [innerHTML]="searchResultItem | cueCustomUnsafeSanitize"
                                      ></div>
                                    }
                                  </div>
                                </div>
                              }
                            </div>

                            @if ((item.qrCodes | canShowPois) && this.overlaysIds().includes(item.resourceId)) {
                              <div
                                (click)="removeOverlay(item.resourceId)"
                                class="appear z-10 absolute inset-0 !bg-white !bg-opacity-80 backdrop-blur-[2px] grid justify-items-center content-center"
                              >
                                <button
                                  size="small"
                                  cue-button
                                  [link]="true"
                                  class="!underline !truncate"
                                  (click)="navigate(item, $event, false)"
                                  [innerHTML]="
                                    (item.displayNameForApp | localizedString) ?? (item.name | localizedString)
                                  "
                                ></button>
                                <div class="flex flex-wrap gap-y-1 gap-x-1 justify-center">
                                  @for (
                                    qrCode of item.qrCodes | poiBoolean: 'showInStartList';
                                    track qrCode;
                                    let last = $last
                                  ) {
                                    <div
                                      class="flex gap-x-1"
                                      [class.selected-item]="qrCode.qrCodeId === targetQrCodeId()"
                                    >
                                      @if (!qrCode.notNavigationPoint) {
                                        <button
                                          size="tiny"
                                          cue-button
                                          [link]="true"
                                          class="!underline !truncate"
                                          (click)="navigateToSpecificQR(item, qrCode.qrCodeId, $event)"
                                        >
                                          <cue-icon [icon]="ICONS.cueQrCode" />
                                          @if ((qrCode.name | localizedString).length <= 15) {
                                            {{ qrCode.name | localizedString }}
                                          } @else {
                                            {{ qrCode.name | localizedString | slice: 0 : 15 }}
                                            <span>...</span>
                                          }
                                        </button>
                                      }
                                      @if (!last) {
                                        <div class="mt-2 inline-block h-4 bg-[--background-divider] w-[1px]"></div>
                                      }
                                    </div>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        } @else {
                          <img src="assets/images/loading.png" width="30" height="30" />
                        }
                      </div>
                    </ng-container>
                  </cdk-virtual-scroll-viewport>
                </div>
                <div class="alphabet-container scrollable-container" cueDragScroll>
                  @for (item of basicListDataSource.value()?.alphabetInfo; track item.letter) {
                    <a
                      [class.not-active-letter]="!item.available"
                      [class.current-letter]="item.letter === currentVisibleLetter()"
                      class="cursor-pointer"
                      (click)="scrollToLetter(item.letter, item.firstIndex)"
                    >
                      {{ item.letter }}
                    </a>
                  }
                </div>
              </div>
            </div>
          }
        }
      }

      @if (selectedSearchType === searchTypeEnum.Nearest && !showNavigationFilter()) {
        @if (nearestOfSortData().loading) {
          <cue-loading />
        }
        @if (!nearestOfSortData().loading) {
          @if (nearestOfSortData().data?.length === 0) {
            <div class="not-found">
              <p class="m-0 py-3">
                {{ translation.notFound.resourceNotFound | transloco }}
              </p>
            </div>
          }
          @if (nearestOfSortData().data?.length !== 0) {
            <div class="location-table h-full overflow-auto location-nearest">
              @for (
                resource of nearestOfSortData().data | filterStartTargetResourceNearest: listType.TargetList;
                track resource;
                let i = $index;
                let last = $last
              ) {
                <div class="relative item-wrapper">
                  <div
                    class="item"
                    [attr.data-cy]="'target-nearest-result-' + resource.resourceId"
                    [class.selected]="
                      (targetResourceId() && resource.resourceId && resource.resourceId === targetResourceId()) ||
                      (targetQrCodeId() &&
                        resource.qrCodes?.length === 1 &&
                        resource.qrCodes[0].qrCodeId === targetQrCodeId())
                    "
                    (click)="navigate(resource, $event, true)"
                  >
                    <div class="item-inner">
                      <div
                        class="preview-image"
                        [class.preview-image--bordered]="
                          resource.icon && resource.file && !hasImageLoadFailed(resource.file)
                        "
                      >
                        @if (resource.file && !hasImageLoadFailed(resource.file)) {
                          <img
                            loading="lazy"
                            [ngStyle]="{
                              'object-position': resource.imageObjectPosition,
                            }"
                            [class.img-cover]="resource.imagePreferCover"
                            [class.category-icon]="resource.fileExtension === 'svg'"
                            [src]="resource.file | imageWidth: 'auto'"
                            (error)="imageLoadFailed(resource.file)"
                          />
                        } @else if (getCategoryIconUrl(resource); as categoryIconUrl) {
                          <img
                            [src]="categoryIconUrl | imageWidth: 'auto'"
                            (error)="imageLoadFailed(categoryIconUrl)"
                          />
                        } @else {
                          <cue-icon
                            [icon]="ICONS.cueQrCode"
                            (click)="navigate(resource, $event, true)"
                            class="qr-img"
                          />
                        }
                        @if (
                          resource.icon &&
                          resource.file &&
                          !hasImageLoadFailed(resource.file) &&
                          !hasImageLoadFailed(resource.icon)
                        ) {
                          <div class="item-icon-wrapper">
                            <img class="item-icon" [src]="resource.icon" (error)="imageLoadFailed(resource.icon)" />
                          </div>
                        }
                      </div>
                      <div class="flex h-full w-full min-w-0 k-gap-1">
                        <div class="header-content h-full w-full overflow-hidden">
                          <div class="line-clamp-2" #targetNearestTitleEl>
                            <span class="rendered-html" data-cy="concrete-target-nearest">
                              {{ (resource.displayNameForApp | localizedString) ?? (resource.name | localizedString) }}
                            </span>
                          </div>
                          <div class="relative max-h-11 flex overflow-hidden">
                            <div
                              class="self-end"
                              [class.truncate]="targetNearestTitleEl.offsetHeight > 30"
                              [class.rtl]="targetNearestTitleEl.offsetHeight > 30"
                            >
                              @for (item of resource.breadcrumbWithIds; track item; let i = $index) {
                                @if (i > 0) {
                                  <span> > </span>
                                }
                                <span>{{ item.item2 | localizedString }}</span>
                              }
                            </div>
                          </div>
                        </div>
                        <div class="flex flex-col items-center gap-[5px]">
                          <button
                            cue-button
                            class="just-icon detail-info-icon"
                            [link]="true"
                            size="small"
                            (click)="openNearestItemDetail(resource, $event)"
                            data-cy="target-show-item-detail-nearest"
                          >
                            <cue-icon [icon]="ICONS.cueInfoNoCircle" />
                          </button>
                          @if (resource.qrCodes.length === 1) {
                            <div
                              class="text-brand-500 font-bold shrink-0 nearestDistance"
                              [attr.data-cy]="'target-nearest-distance-' + resource.resourceId"
                            >
                              {{ resource.qrCodes[0].distance | number: '1.0-0' : 'en' }}m
                            </div>
                          }
                        </div>
                      </div>
                    </div>

                    @if (search?.value) {
                      <div
                        class="search-result"
                        [class.show]="resource.searchResultIsShown"
                        (click)="toggleShowNearest(resource, $event)"
                      >
                        <span class="label">{{ translation.wayfinding.searched | transloco }}: </span>
                        <div class="search-result-items">
                          @for (searchResultItem of resource | searchResult: search?.value; track searchResultItem) {
                            <div
                              class="search-result-item"
                              [innerHTML]="searchResultItem | cueCustomUnsafeSanitize"
                            ></div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                  @if (!last) {
                    <hr class="my-[5px]" />
                  }
                  @if ((resource.qrCodes | canShowPois) && nearestItemsOverlaysIds.includes(resource.resourceId)) {
                    <div
                      (click)="nearestItemRemoveOverlay(resource.resourceId)"
                      class="appear z-10 absolute inset-0 !bg-white !bg-opacity-80 backdrop-blur-[2px] grid justify-items-center content-center"
                    >
                      <button
                        size="small"
                        cue-button
                        [link]="true"
                        class="!underline !truncate"
                        (click)="navigate(resource, $event, true)"
                      >
                        {{ (resource.displayNameForApp | localizedString) ?? (resource.name | localizedString) }}
                      </button>
                      <div class="flex flex-wrap gap-y-1 gap-x-1 justify-center">
                        @if (resource.qrCodes | canShowPois) {
                          <div class="entrances">
                            @for (
                              qrCode of resource.qrCodes | poiBoolean: 'showInTargetList';
                              track qrCode;
                              let last = $last
                            ) {
                              <button
                                size="tiny"
                                cue-button
                                [link]="true"
                                class="!underline !truncate"
                                (mousedown)="navigateToSpecificQR(resource, qrCode.qrCodeId, $event)"
                              >
                                <cue-icon [icon]="ICONS.cueQrCode" />
                                @if ((qrCode.name | localizedString).length <= 15) {
                                  {{ qrCode.name | localizedString }}
                                } @else {
                                  {{ qrCode.name | localizedString | slice: 0 : 15 }}
                                  <span>...</span>
                                }

                                <span class="w-px h-4 bg-neutral-300 my-2"></span>
                                <span class="">
                                  {{ qrCode.distance || 0 | number: '1.0-0' : 'en' }}
                                  m
                                </span>
                              </button>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          }
        }
      }
    </div>
  </div>
</div>
@if (showNavigationFilter()) {
  <div class="assist-target-filter-wrapper">
    <cue-start-target-filter
      [selectedResourceTypeIdList]="selectedResourceTypeIdList()"
      [selectedCategoryIdList]="selectedCategoryIdList()"
      [selectedAreaIdList]="selectedAreaIdList()"
      [availableCategoryList]="categories.value()"
      [availableAreaList]="availableAreaList() | areaToHierarchy"
      (requestClose)="changeShowFilter(false)"
      (confirmFilter)="updateData($event)"
      [isKioskMode]="isKioskMode()"
    />
  </div>
}
